import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, TextField, Chip, Box, Typography, CircularProgress
} from '@mui/material';

const getTacobellEmails = async () => {
  const baseURL = window.location.hostname === 'localhost' 
    ? 'http://localhost:5001/' 
    : 'https://api.delightrgext.xyz/';
  const response = await fetch(`${baseURL}admin/tacobell-emails`);
  return response.json();
};

const updateTacobellEmails = async (data: any) => {
  const baseURL = window.location.hostname === 'localhost' 
    ? 'http://localhost:5001/' 
    : 'https://api.delightrgext.xyz/';
  const response = await fetch(`${baseURL}admin/tacobell-emails`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return response.json();
};

const EmailConfiguration: React.FC = () => {
  const [emails, setEmails] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({ id: null, city: '', botType: '', toEmails: '', ccEmails: '' });
  const [editingId, setEditingId] = useState<number | null>(null);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await getTacobellEmails();
      setEmails(response.data || []);
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (email: any) => {
    setEditingId(email.id);
    setEditData({
      id: email.id,
      city: email.City,
      botType: email.BotType,
      toEmails: email.TO_Emails,
      ccEmails: email.CC_Emails || ''
    });
  };

  const handleSave = async () => {
    try {
      await updateTacobellEmails(editData);
      setEditingId(null);
      fetchEmails();
    } catch (error) {
      console.error('Failed to update email:', error);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  return (
    <div className="mt-4">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#000', fontWeight: '600' }}>
          Client Email Configuration
        </Typography>
        <Button
          variant="contained"
          onClick={fetchEmails}
          disabled={loading}
          sx={{
            backgroundColor: '#000',
            '&:hover': {
              backgroundColor: '#333'
            },
            textTransform: 'none',
            fontWeight: '500',
            borderRadius: '12px'
          }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Refresh Data'}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#000' }} />
        </Box>
      ) : emails.length > 0 ? (
        <TableContainer component={Paper} sx={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb' }}>City</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb' }}>Bot Type</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb' }}>To Emails</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb' }}>CC Emails</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emails.map((email, index) => (
                <TableRow key={index} className="hover:bg-gray-50">
                  <TableCell sx={{ color: '#111827' }}>{email.id}</TableCell>
                  <TableCell sx={{ color: '#111827' }}>{email.City}</TableCell>
                  <TableCell sx={{ color: '#111827' }}>{email.BotType}</TableCell>
                  <TableCell>
                    {editingId === email.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editData.toEmails}
                        onChange={(e) => setEditData({...editData, toEmails: e.target.value})}
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{email.TO_Emails}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === email.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editData.ccEmails}
                        onChange={(e) => setEditData({...editData, ccEmails: e.target.value})}
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{email.CC_Emails || '—'}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={email.IsActive ? 'Active' : 'Inactive'}
                      color={email.IsActive ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {editingId === email.id ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSave}
                          sx={{ backgroundColor: '#10b981', color: '#fff', '&:hover': { backgroundColor: '#059669' } }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setEditingId(null)}
                          sx={{ color: '#6b7280', borderColor: '#d1d5db', '&:hover': { borderColor: '#9ca3af' } }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEdit(email)}
                        sx={{ color: '#000', borderColor: '#000', borderRadius: '8px', '&:hover': { backgroundColor: '#f3f4f6', borderColor: '#000' } }}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '16px' }}>
          <Typography sx={{ color: '#6b7280' }}>No client emails found.</Typography>
        </Paper>
      )}
    </div>
  );
};

export default EmailConfiguration;
